import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ContentCard from '@/components/feature/ContentCard';
import EmptyState from '@/components/base/EmptyState';
import SharePanel from './components/SharePanel';
import TopicReplies from './components/TopicReplies';
import VideoPlayer from './components/VideoPlayer';
import { useSEO } from '@/hooks/useSEO';
import { useFavorites } from '@/hooks/useFavorites';
import { getContentById, getRelated, formatViews, type ContentItem } from '@/hooks/useContent';

const typeConfig: Record<ContentItem['type'], { label: string; icon: string; to: string; crumb: string; badge: string }> = {
  news: { label: '娱乐资讯', icon: 'ri-newspaper-line', to: '/category/news', crumb: '娱乐资讯', badge: 'bg-primary-100 text-primary-700' },
  video: { label: '视频', icon: 'ri-play-circle-line', to: '/category/video', crumb: '视频', badge: 'bg-accent-100 text-accent-800' },
  topic: { label: '互动话题', icon: 'ri-chat-3-line', to: '/category/topic', crumb: '互动话题', badge: 'bg-secondary-100 text-secondary-800' },
  special: { label: '专题', icon: 'ri-bookmark-3-line', to: '/special', crumb: '精选专题', badge: 'bg-primary-100 text-primary-700' },
};

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getContentById(id) : undefined;
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState('');

  useSEO(
    item ? `${item.title} - 一起艹` : '内容不存在 - 一起艹',
    item
      ? (item.summary || item.body[0] || '一起艹为你呈现精彩娱乐内容。')
      : '抱歉，你访问的内容不存在或已被移除，可返回首页继续浏览其他精彩内容。',
    item ? `${item.tags.join(',')},一起艹` : '一起艹,娱乐',
  );

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-16 md:py-24">
        <EmptyState
          icon="ri-error-warning-line"
          title="内容不存在或已下架"
          description="你访问的页面可能已被移除，或链接输入有误。可以返回首页继续浏览其他精彩内容。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE?.('/');
          }}
        />
      </div>
    );
  }

  const config = typeConfig[item.type];
  const related = getRelated(item, 4);
  const favorited = isFavorite(item.id);
  const videoUrl = 'videoUrl' in item ? item.videoUrl : undefined;
  const duration = 'duration' in item ? item.duration : undefined;

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb
        items={[{ label: '首页', to: '/' }, { label: config.crumb, to: config.to }, { label: item.title }]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
        <article className="lg:col-span-8 min-w-0">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.badge}`}>
            <i className={config.icon} />
            {config.label}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl text-foreground-950 leading-snug">{item.title}</h1>

          <div className="mt-4 flex items-center flex-wrap gap-x-4 gap-y-2 text-xs md:text-sm text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line" />
              {item.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {item.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {item.type === 'topic' && 'participants' in item
                ? `${formatViews(item.participants)} 人参与`
                : `${formatViews(item.views)} 阅读`}
            </span>
            {duration ? (
              <span className="flex items-center gap-1.5 text-accent-700">
                <i className="ri-timer-line" />
                {duration}
              </span>
            ) : null}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-accent-100 hover:text-accent-800 cursor-pointer transition-colors"
              >
                # {tag}
              </Link>
            ))}
          </div>

          <div className="mt-6">
            {videoUrl ? (
              <VideoPlayer src={videoUrl} poster={item.cover} title={item.title} />
            ) : (
              <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 一起艹`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}
          </div>

          <div className="mt-7 space-y-4">
            {item.body.map((paragraph, index) => (
              <p key={index} className="text-sm md:text-base text-foreground-800 leading-loose">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex items-center flex-wrap gap-3 pb-8 border-b border-background-200">
            <SharePanel title={item.title} onNotify={setToast} />
            <button
              type="button"
              onClick={() => {
                toggle(item.id);
                setToast(favorited ? '已取消收藏' : '已加入收藏（仅保存在本地浏览器）');
              }}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md border text-sm whitespace-nowrap cursor-pointer transition-colors ${
                favorited
                  ? 'border-accent-400 bg-accent-100 text-accent-900'
                  : 'border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              <i className={favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
              {favorited ? '已收藏' : '收藏'}
            </button>
            <Link
              to={config.to}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line" />
              返回{config.crumb}
            </Link>
          </div>

          {item.type === 'topic' && 'replies' in item ? (
            <TopicReplies replies={item.replies} onNotify={setToast} />
          ) : null}
        </article>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <h2 className="flex items-center gap-2 font-heading text-lg md:text-xl text-foreground-950 mb-4">
              <i className="ri-magic-line text-primary-500" />
              {item.type === 'special' ? '专题收录内容' : '相关内容推荐'}
            </h2>
            <div className="flex flex-col gap-3">
              {related.map((entry) => (
                <ContentCard key={entry.id} item={entry} variant="wide" />
              ))}
            </div>

            <div className="mt-6 p-5 rounded-lg bg-secondary-900 text-background-100">
              <h3 className="font-heading text-lg text-background-50">发现更多精彩</h3>
              <p className="mt-2 text-sm text-secondary-200">去专题页看看我们精心策划的内容合集。</p>
              <Link
                to="/special"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                浏览精选专题
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-md bg-secondary-900 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      ) : null}
    </div>
  );
}